import { useState, useEffect } from 'react';
import { contestApi, paymentApi, refundApi, winnerApi, dashboardApi } from '../utils/adminApi';

// Custom hook for contest management
export const useContests = () => {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContests = async () => {
    try {
      setLoading(true);
      const data = await contestApi.getAll();
      setContests(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching contests:', err);
    } finally {
      setLoading(false);
    }
  };

  const createContest = async (contestData) => {
    try {
      const newContest = await contestApi.create(contestData);
      setContests(prev => [...prev, newContest]);
      return newContest;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateContest = async (id, contestData) => {
    try {
      const updatedContest = await contestApi.update(id, contestData);
      setContests(prev => prev.map(contest => 
        contest.id === id ? updatedContest : contest
      ));
      return updatedContest;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateContestStatus = async (id, status) => {
    try {
      await contestApi.updateStatus(id, status);
      setContests(prev => prev.map(contest => 
        contest.id === id ? { ...contest, status } : contest
      ));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteContest = async (id) => {
    try {
      await contestApi.delete(id);
      setContests(prev => prev.filter(contest => contest.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchContests();
  }, []);

  return {
    contests,
    loading,
    error,
    fetchContests,
    createContest,
    updateContest,
    updateContestStatus,
    deleteContest
  };
};

// Custom hook for payment management
export const usePayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await paymentApi.getAll();
      setPayments(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  return {
    payments,
    loading,
    error,
    fetchPayments
  };
};

// Custom hook for refund management
export const useRefunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRefunds = async () => {
    try {
      setLoading(true);
      const data = await refundApi.getAll();
      setRefunds(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching refunds:', err);
    } finally {
      setLoading(false);
    }
  };

  const createRefund = async (refundData) => {
    try {
      const newRefund = await refundApi.create(refundData);
      setRefunds(prev => [...prev, newRefund]);
      return newRefund;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, []);

  return {
    refunds,
    loading,
    error,
    fetchRefunds,
    createRefund
  };
};

// Custom hook for winner management
export const useWinners = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWinners = async () => {
    try {
      setLoading(true);
      const data = await winnerApi.getAll();
      setWinners(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching winners:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateWinnerStatus = async (winnerId, status) => {
    try {
      await winnerApi.updateStatus(winnerId, status);
      setWinners(prev => prev.map(winner => 
        winner.id === winnerId ? { ...winner, status } : winner
      ));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchWinners();
  }, []);

  return {
    winners,
    loading,
    error,
    fetchWinners,
    updateWinnerStatus
  };
};

// Custom hook for dashboard metrics
export const useDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getMetrics();
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching dashboard metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    
    // Auto-refresh metrics every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  return {
    metrics,
    loading,
    error,
    fetchMetrics
  };
};